import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import Toast from '../../components/ui/Toast';

import TournamentSelector from './components/TournamentSelector';
import TeamSelector from './components/TeamSelector';
import PlayerCard from './components/PlayerCard';
import SquadPanel from './components/SquadPanel';
import { tournamentsService, teamsService, playersService, playerTeamAssignmentsService } from '../../services/supabaseService';

const TournamentSquadManagement = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tournaments, setTournaments] = useState([]);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [tournamentTeams, setTournamentTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [availablePlayers, setAvailablePlayers] = useState([]);
  const [squadPlayers, setSquadPlayers] = useState([]);
  const [pendingChanges, setPendingChanges] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [toast, setToast] = useState(null);

  useEffect(() => {
    fetchTournaments();
  }, []);

  useEffect(() => {
    if (selectedTournament) {
      fetchTournamentTeams();
    }
  }, [selectedTournament]);

  useEffect(() => {
    if (selectedTeam && selectedTournament) {
      fetchPlayersAndSquad();
    }
  }, [selectedTeam, selectedTournament]);

  const fetchTournaments = async () => {
    setLoading(true);
    try {
      const data = await tournamentsService?.getAll();
      setTournaments(data || []);
      
      const tournamentId = searchParams?.get('tournamentId');
      if (tournamentId && data?.length > 0) {
        const tournament = data?.find(t => t?.id === tournamentId);
        if (tournament) {
          setSelectedTournament(tournament);
        }
      }
    } catch (error) {
      console.error('Error fetching tournaments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTournamentTeams = async () => {
    try {
      const teams = await teamsService?.getByTournament(selectedTournament?.id);
      setTournamentTeams(teams?.map(tt => tt?.teams) || []);
    } catch (error) {
      console.error('Error fetching tournament teams:', error);
    }
  };

  const fetchPlayersAndSquad = async () => {
    try {
      let allPlayers;

      if (selectedTournament?.matchType === 'League') {
        // For league tournaments, fetch players from player_team_assignments
        const assignments = await playerTeamAssignmentsService?.getActiveByTeam(selectedTeam?.id);
        allPlayers = assignments
          ?.filter(a => a?.player)
          ?.map(a => ({
            id: a?.player?.id,
            name: a?.player?.name,
            role: a?.player?.role,
            battingStyle: a?.player?.battingStyle,
            bowlingStyle: a?.player?.bowlingStyle,
            jerseyNumber: a?.player?.jerseyNumber,
            teamId: a?.player?.teamId
          })) || [];
      } else {
        allPlayers = await playersService?.getByTeam(selectedTeam?.id);
      }

      const squad = await playersService?.getTournamentSquad(selectedTournament?.id, selectedTeam?.id);
      
      const squadPlayerIds = new Set(squad?.map(p => p?.playerId));
      
      setAvailablePlayers(allPlayers?.filter(p => !squadPlayerIds?.has(p?.id)) || []);
      setSquadPlayers(squad || []);
    } catch (error) {
      console.error('Error fetching players:', error);
    }
  };

  const filteredAvailablePlayers = availablePlayers?.filter(player => {
    const matchesSearch = player?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesRole = filterRole === 'all' || player?.role === filterRole;
    return matchesSearch && matchesRole;
  });

  const handleAddToSquad = async (player) => {
    // Add to pending changes instead of immediate save
    setAvailablePlayers(availablePlayers?.filter(p => p?.id !== player?.id));
    setSquadPlayers([...squadPlayers, { ...player, playerId: player?.id }]);
    setPendingChanges([...pendingChanges, { type: 'add', player }]);
  };

  const handleAddAllToSquad = () => {
    if (filteredAvailablePlayers?.length === 0) return;
    const maxSquadSize = 25;
    const remainingSlots = maxSquadSize - squadPlayers?.length;
    if (remainingSlots <= 0) {
      setToast({ message: 'Squad is full. Remove players to add new ones.', type: 'error' });
      return;
    }
    const playersToAdd = filteredAvailablePlayers?.slice(0, remainingSlots);
    const playerIdsToAdd = new Set(playersToAdd?.map(p => p?.id));
    setAvailablePlayers(availablePlayers?.filter(p => !playerIdsToAdd?.has(p?.id)));
    setSquadPlayers([...squadPlayers, ...playersToAdd?.map(p => ({ ...p, playerId: p?.id }))]);
    setPendingChanges([...pendingChanges, ...playersToAdd?.map(p => ({ type: 'add', player: p }))]);
    if (playersToAdd?.length < filteredAvailablePlayers?.length) {
      setToast({ message: `Added ${playersToAdd?.length} players. Squad is now full (${maxSquadSize}/${maxSquadSize}).`, type: 'warning' });
    }
  };

  const handleRemoveFromSquad = async (player) => {
    // Add to pending changes instead of immediate save
    setSquadPlayers(squadPlayers?.filter(p => p?.playerId !== player?.playerId));
    setAvailablePlayers([...availablePlayers, { ...player, id: player?.playerId }]);
    setPendingChanges([...pendingChanges, { type: 'remove', player }]);
  };

  const handleSaveChanges = async () => {
    if (pendingChanges?.length === 0) return;
    
    setIsSaving(true);
    try {
      for (const change of pendingChanges) {
        if (change?.type === 'add') {
          await playersService?.addToTournamentSquad(
            selectedTournament?.id,
            selectedTeam?.id,
            change?.player?.id
          );
        } else if (change?.type === 'remove') {
          await playersService?.removeFromTournamentSquad(
            selectedTournament?.id,
            selectedTeam?.id,
            change?.player?.playerId
          );
        }
      }
      setPendingChanges([]);
      setToast({ message: 'Squad changes saved successfully!', type: 'success' });
    } catch (error) {
      console.error('Error saving squad changes:', error);
      setToast({ message: 'Failed to save changes. Please try again.', type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscardChanges = () => {
    if (pendingChanges?.length === 0) return;
    if (window.confirm('Are you sure you want to discard all unsaved changes?')) {
      fetchPlayersAndSquad();
      setPendingChanges([]);
    }
  };

  const roleOptions = [
    { value: 'all', label: 'All Roles' },
    { value: 'Batter', label: 'Batter' },
    { value: 'Bowler', label: 'Bowler' },
    { value: 'Wicket Keeper', label: 'Wicket Keeper' },
    { value: 'All-rounder', label: 'All-rounder' }
  ];

  const squadByRole = {
    'Batter': squadPlayers?.filter(p => p?.role === 'Batter'),
    'Bowler': squadPlayers?.filter(p => p?.role === 'Bowler'),
    'Wicket Keeper': squadPlayers?.filter(p => p?.role === 'Wicket Keeper'),
    'All-rounder': squadPlayers?.filter(p => p?.role === 'All-rounder')
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="main-content">
        <div className="container mx-auto px-4 py-6 md:py-8">
        {/* Page Header */}
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(selectedTournament?.id ? `/tournament-management/${selectedTournament?.id}` : '/admin-dashboard')}
                  className="flex items-center gap-2"
                >
                  <Icon name="ArrowLeft" size={16} />
                  Back to Tournament Management
                </Button>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">Tournament Squad Management</h1>
              <p className="text-sm text-muted-foreground">Configure tournament-specific player squads for each team</p>
            </div>
            {pendingChanges?.length > 0 && (
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm text-yellow-600 dark:text-yellow-400 font-medium">
                  {pendingChanges?.length} unsaved change{pendingChanges?.length !== 1 ? 's' : ''}
                </span>
                <Button
                  variant="outline"
                  onClick={handleDiscardChanges}
                  disabled={isSaving}
                >
                  Discard
                </Button>
                <Button
                  onClick={handleSaveChanges}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Selectors */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <TournamentSelector
            tournaments={tournaments}
            selectedTournament={selectedTournament}
            onSelect={setSelectedTournament}
          />
          
          {selectedTournament && (
            <TeamSelector
              teams={tournamentTeams}
              selectedTeam={selectedTeam}
              onSelect={setSelectedTeam}
            />
          )}
        </div>

        {selectedTournament && selectedTeam ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Available Players Panel */}
            <div className="bg-card rounded-lg shadow-sm border border-border p-6">
              <div className="mb-4">
                <h2 className="text-lg font-semibold text-foreground mb-1">Available Players</h2>
                {selectedTournament?.matchType === 'League' && (
                  <p className="text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-3 py-1.5 rounded-md mb-3 flex items-center gap-1.5">
                    <Icon name="Info" size={13} />
                    Showing players from league team roster assignments
                  </p>
                )}
                
                <div className="space-y-3">
                  <Input
                    type="text"
                    placeholder="Search players..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    iconName="Search"
                  />
                  
                  <select
                    value={filterRole}
                    onChange={(e) => setFilterRole(e?.target?.value)}
                    className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary">
                    {roleOptions?.map(option => (
                      <option key={option?.value} value={option?.value}>
                        {option?.label}
                      </option>
                    ))}
                  </select>

                  {filteredAvailablePlayers?.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleAddAllToSquad}
                      className="w-full flex items-center justify-center gap-2"
                    >
                      <Icon name="PlusCircle" size={15} />
                      Add All ({filteredAvailablePlayers?.length}) to Squad
                    </Button>
                  )}
                </div>
              </div>

              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {filteredAvailablePlayers?.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Icon name="Users" className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No available players</p>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-sm text-muted-foreground">Add players to squad:</span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleAddAllToSquad}
                      >
                        Add All
                      </Button>
                    </div>
                    {filteredAvailablePlayers?.map((player) => (
                      <PlayerCard
                        key={player?.id}
                        player={player}
                        onAction={() => handleAddToSquad(player)}
                        actionIcon="Plus"
                        actionLabel="Add to Squad"
                      />
                    ))}
                  </>
                )}
              </div>
            </div>

            {/* Squad Panel */}
            <SquadPanel
              squadPlayers={squadPlayers}
              squadByRole={squadByRole}
              onRemovePlayer={handleRemoveFromSquad}
              tournament={selectedTournament}
            />
          </div>
        ) : (
          <div className="bg-card rounded-lg shadow-sm border border-border p-12 text-center">
            <Icon name="Users" className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-semibold text-foreground mb-2">Select Tournament and Team</h3>
            <p className="text-muted-foreground">
              Choose a tournament and team to manage their squad
            </p>
          </div>
        )}
      </div>
      {toast && (
        <Toast
          message={toast?.message}
          type={toast?.type}
          onClose={() => setToast(null)}
        />
      )}
    </main>
    </div>
  );
};

export default TournamentSquadManagement;